import { Divider, Grid, Typography } from '@material-ui/core';
import { AddCircle, AccountBalance, Delete } from '@material-ui/icons';
import { Option } from 'common/genericOptions';
import { BancoResponse, Comprovante, Custodiante, DelimitacaoCoordenadorResponse, InformacoesBancariasCustodiante, InstrumentoFinanceiro, InvestidorResponse } from 'common/genericTypes';
import { Button, LgSpace, SmSpace } from 'm2m.components';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { OfertaPrimariaInvestidor as BaseOfertaPrimariaInvestidor, updateOfertaPrimariaField, removeOfertaPrimariaField } from 'store/modules/operacoes/registro';

interface OfertaPrimariaInvestidor extends BaseOfertaPrimariaInvestidor {
  fromXLSX?: boolean;
}
import { addCpfCnpjMask } from 'utils/mask';
import InvestidorDrawer from './investidorDrawer';
import InformacoesBancariasAddDrawer from 'components/informacoesBancariasDrawer/informacoesBancariasAddDrawer';
import InformacoesBancariasListDrawer from 'components/informacoesBancariasDrawer/informacoesBancariasListDrawer';
import ComprovanteComponent from './comprovante';
import { formatEightDecimalsCurrency, formatNumber } from 'utils/number';

export interface InvestidorMovimentacaoData {
  id: string;
  cpfCnpj: string;
  quantidade: string;
  pu: string;
}

interface Props {
  isFulfilled(fulFilled: boolean): void;
  attachments: Array<Comprovante>;
  onAttachments: (anexo: Array<Comprovante>) => void;
  investidoresXLSX?: Array<InvestidorMovimentacaoData>;
  ocultarDadosBancarios?: boolean;
  delimitacaoCoordenador?: Array<DelimitacaoCoordenadorResponse>;
}

const AdicionarInvestidor: React.FC<Props> = (props): React.ReactElement => {
  const { isFulfilled, onAttachments, attachments, investidoresXLSX, ocultarDadosBancarios, delimitacaoCoordenador } = props;
  
  const [exceedsDelimitacao, setExceedsDelimitacao] = useState<boolean>(false);
  const [exceedsValorDelimitacao, setExceedsValorDelimitacao] = useState<boolean>(false);
  const [totalQuantidade, setTotalQuantidade] = useState<number>(0);
  const [totalValor, setTotalValor] = useState<number>(0);
  const [maxQuantidade, setMaxQuantidade] = useState<number>(0);
  const [maxValor, setMaxValor] = useState<number>(0);
  const [delimitacaoErrorMessage, setDelimatacaoErrorMessage] = useState<string>('');

  const [openInvestidorDrawer, setOpenInvestidorDrawer] = useState<boolean>(false);
  const [openInformacoesBancariasDrawer, setOpenInformacoesBancariasDrawer] = useState<boolean>(false);
  const [openInformacoesBancariasListDrawer, setOpenInformacoesBancariasListDrawer] = useState<boolean>(false);
  const [investidores, setInvestidores] = useState<Array<InvestidorResponse>>([]);
  const [investidorOptions, setInvestidorOptions] = useState<Array<Option>>([]);
  const [bancos, setBancos] = useState<Array<BancoResponse>>([]);
  const [isEditInformacoesBancarias, setIsEditInformacoesBancarias] = useState<boolean>(false);

  const dispatch = useDispatch();
  const getInvestidoresApi = useSelector((state: RootState) => state.investidorApi.getInvestidores);
  const ofertaPrimaria = useSelector((state: RootState) => state.operacao.ofertaPrimaria);
  const tipoInstrumentoFinanceiro = useSelector((state: RootState) => state.operacao.tipoInstrumentoFinanceiro);
  const currentParticipante = useSelector((state: RootState) => state.participante);
  const getBancosApi = useSelector((state: RootState) => state.catalogoApi.getBancos);

  const getInvestidores = useCallback(async () => {
    try {
      const result = await getInvestidoresApi(dispatch, undefined, true);
      if (result) setInvestidores(result);
    } catch { }
  }, [dispatch, getInvestidoresApi]);

  const getBancos = useCallback(async () => {
    try {
      const result = await getBancosApi(dispatch, true);
      if (!result || !result.banks || !result.banks.length) return;
      setBancos(result.banks);
    } catch (error) { }
  }, [dispatch, getBancosApi]);

  useEffect(() => {
    getInvestidores();
    getBancos();
  }, [getInvestidores]);

  useEffect(() => {
    if (investidores) {
      const result: Array<Option> = investidores.map((x) => {
        return { description: addCpfCnpjMask(x.cpfCnpj), value: x.id };
      });
      setInvestidorOptions(result);
    }
  }, [investidores, setInvestidorOptions]);

  const [processedXLSXData, setProcessedXLSXData] = useState(false);
  const [previousXLSXData, setPreviousXLSXData] = useState<Array<InvestidorMovimentacaoData> | undefined>(undefined);

  useEffect(() => {
    if (investidoresXLSX !== previousXLSXData) {
      setPreviousXLSXData(investidoresXLSX);
      setProcessedXLSXData(false);

      if (!investidoresXLSX || investidoresXLSX.length === 0) {
        dispatch(
          updateOfertaPrimariaField({
            name: 'investidores',
            value: [],
          })
        );
      }
    }
  }, [investidoresXLSX, previousXLSXData, dispatch]);

  useEffect(() => {
    try {
      if (investidoresXLSX && investidoresXLSX.length > 0 && investidores && investidores.length > 0 && !processedXLSXData) {
        const newInvestidoresToAdd: OfertaPrimariaInvestidor[] = [];

        investidoresXLSX.forEach((xlsxInvestidor: InvestidorMovimentacaoData) => {
          const alreadyAdded = ofertaPrimaria?.investidores.some(
            existingInvestidor => existingInvestidor.cpfCnpj === xlsxInvestidor.cpfCnpj
          );

          if (!alreadyAdded && xlsxInvestidor.cpfCnpj) {
            const matchedInvestidor = investidores.find(inv => inv.cpfCnpj === xlsxInvestidor.cpfCnpj);

            if (matchedInvestidor) {
              const newInvestidor: OfertaPrimariaInvestidor = {
                investidorId: matchedInvestidor.id,
                cpfCnpj: matchedInvestidor.cpfCnpj,
                contaDeposito: matchedInvestidor.contaDeposito || '',
                quantidade: Number(xlsxInvestidor.quantidade) || 0,
                precoUnitario: Number(xlsxInvestidor.pu) || 0
              };

              newInvestidoresToAdd.push(newInvestidor);
            }
          }
        });

        if (newInvestidoresToAdd.length > 0) {
          dispatch(
            updateOfertaPrimariaField({
              name: 'investidores',
              value: [...(ofertaPrimaria?.investidores ?? []), ...newInvestidoresToAdd],
            })
          );
          onAttachments(attachments);
        }
        setProcessedXLSXData(true);
      }
    } catch (error) {
      console.error('Error processing XLSX data:', error);
    }
  }, [investidoresXLSX, investidores, ofertaPrimaria?.investidores, dispatch, onAttachments, attachments, processedXLSXData]);

  const handleInvestidorDrawerClose = () => setOpenInvestidorDrawer(false);

  const handleOfertaPrimariaSubmit = (investidor: OfertaPrimariaInvestidor) => {
    dispatch(
      updateOfertaPrimariaField({
        name: 'investidores',
        value: [...(ofertaPrimaria?.investidores ?? []), investidor],
      })
    );
    onAttachments(attachments);
    handleInvestidorDrawerClose();
  };

  const handleOfertaPrimariaRemove = (investidor: OfertaPrimariaInvestidor) => {
    const currentInvestidores = [...(ofertaPrimaria?.investidores || [])];

    const indexToRemove = currentInvestidores.findIndex(inv =>
      (inv.cpfCnpj && investidor.cpfCnpj && inv.cpfCnpj === investidor.cpfCnpj) ||
      inv.investidorId === investidor.investidorId
    );

    if (indexToRemove !== -1) {
      currentInvestidores.splice(indexToRemove, 1);

      dispatch(
        updateOfertaPrimariaField({
          name: 'investidores',
          value: currentInvestidores,
        })
      );
    }

    onAttachments(attachments);
    handleInvestidorDrawerClose();
  };

  const handleOpenInformacoesBancariasListDrawer = () => {
    if ((ofertaPrimaria?.informacoesBancarias ?? []).length > 0) {
      setOpenInformacoesBancariasDrawer(false);
      setOpenInformacoesBancariasListDrawer(true);
    } else {
      setOpenInformacoesBancariasListDrawer(false);
      setOpenInformacoesBancariasDrawer(true);
    }
  }

  const handleCloseInformacoesBancariasListDrawer = () => {
    setOpenInformacoesBancariasListDrawer(false);
  }

  const handleCloseInformacoesBancariasDrawer = () => {
    setOpenInformacoesBancariasDrawer(false);
  }

  const handleOpenAddInformacoesBancariasDrawer = () => {
    if (bancos.length === 0) {
      getBancos();
    }
    setOpenInformacoesBancariasListDrawer(false);
    setOpenInformacoesBancariasDrawer(true);
    setIsEditInformacoesBancarias(false);
  }

  const handleUpdateInformacoesBancarias = (informacoesBancarias?: InformacoesBancariasCustodiante) => {
    let dadosBancarios: InformacoesBancariasCustodiante[] = [];
    if (informacoesBancarias) {
      dadosBancarios.push(informacoesBancarias);
    }
    dispatch(
      updateOfertaPrimariaField({
        name: 'informacoesBancarias',
        value: [...dadosBancarios],
      })
    );
  }

  const handleAddinformacoesBancarias = async (value: InformacoesBancariasCustodiante, isEdit: boolean) => {
    handleUpdateInformacoesBancarias(value);
  }

  useEffect(() => {
    if (delimitacaoCoordenador && delimitacaoCoordenador.length > 0 && ofertaPrimaria?.investidores) {
      const quantidadeDelimitacao = delimitacaoCoordenador
        .filter(d => d.tipoDelimitacao === 'QUANTIDADE')
        .sort((a, b) => (a.isCoordenadorLider === b.isCoordenadorLider) ? 0 : a.isCoordenadorLider ? -1 : 1)[0];
      
      const valorDelimitacao = delimitacaoCoordenador
        .filter(d => d.tipoDelimitacao === 'VALOR')
        .sort((a, b) => (a.isCoordenadorLider === b.isCoordenadorLider) ? 0 : a.isCoordenadorLider ? -1 : 1)[0];

      const totalQuantidade = ofertaPrimaria.investidores.reduce((sum, investidor) => {
        const quantidade = Number(investidor.quantidade || 0);
        return sum + (isNaN(quantidade) ? 0 : quantidade);                                                                       
      }, 0);
      
      const totalValor = ofertaPrimaria.investidores.reduce((sum, investidor) => {
        const quantidade = Number(investidor.quantidade || 0);
        const precoUnitario = Number(investidor.precoUnitario || 0);
        return sum + (isNaN(quantidade) || isNaN(precoUnitario) ? 0 : quantidade * precoUnitario);
      }, 0);
      
      setTotalQuantidade(totalQuantidade);
      setTotalValor(totalValor);
      
      if (quantidadeDelimitacao && quantidadeDelimitacao.valor !== undefined && quantidadeDelimitacao.valor !== null) {
        setMaxQuantidade(quantidadeDelimitacao.valor);
        setExceedsDelimitacao(totalQuantidade > Number(quantidadeDelimitacao.valor));
      } else {
        setMaxQuantidade(0);
        setExceedsDelimitacao(false);
      }
      
      if (valorDelimitacao && valorDelimitacao.valor !== undefined && valorDelimitacao.valor !== null) {
        setMaxValor(valorDelimitacao.valor);
        setExceedsValorDelimitacao(totalValor > Number(valorDelimitacao.valor));
      } else {
        setMaxValor(0);
        setExceedsValorDelimitacao(false);
      }
    } else {
      setTotalQuantidade(0);
      setTotalValor(0);
      setMaxQuantidade(0);
      setMaxValor(0);
      setExceedsDelimitacao(false);
      setExceedsValorDelimitacao(false);
    }
  }, [delimitacaoCoordenador, ofertaPrimaria?.investidores]);
  
  useEffect(() => {
    if (isFulfilled) {
      isFulfilled(!!ofertaPrimaria && Array.isArray(ofertaPrimaria.investidores) && ofertaPrimaria.investidores.length > 0);
    }
  }, [isFulfilled, ofertaPrimaria]);

  const columns = 2
  return (
    <div style={{ marginRight: 40 }}>
      <SmSpace />
      <Divider variant="fullWidth" />
      <SmSpace />
      <Button
        style={{ marginRight: '10px' }}
        variant="contained"
        size="small"
        color="primary"
        onClick={() => {
          setOpenInvestidorDrawer(true);
        }}
      >
        <AddCircle fontSize="small" style={{ marginRight: '10px' }} /> {'Adicionar Investidor'}
      </Button>
      {!ocultarDadosBancarios && (
        <Button
          variant="contained"
          size="small"
          color="secondary"
          onClick={() => {
            handleOpenInformacoesBancariasListDrawer();
          }}
        >
          <AccountBalance fontSize="small" style={{ marginRight: '10px' }} /> {'Dados Bancários'}
        </Button>
      )}

      <LgSpace />
      
      {delimitacaoErrorMessage && (
        <div style={{ backgroundColor: '#f8d7da', color: '#721c24', padding: '15px', borderRadius: '4px', marginBottom: '20px', textAlign: 'center', width: '100%', fontSize: '16px', fontWeight: 'bold' }}>
          <Typography variant="body1">
            <strong>Erro:</strong> {delimitacaoErrorMessage}
          </Typography>
        </div>
      )}
      
      {(exceedsDelimitacao || exceedsValorDelimitacao) && (
        <div style={{ backgroundColor: '#FFF3CD', color: '#856404', padding: '10px', marginBottom: '15px', borderRadius: '4px', border: '1px solid #FFEEBA' }}>
          <Typography variant="body2">
            <strong>Atenção:</strong> 
            {exceedsDelimitacao && exceedsValorDelimitacao ? (
              <>Os limites de quantidade e valor definidos pelo coordenador foram excedidos.</>  
            ) : exceedsDelimitacao ? (
              <>A quantidade total de investimentos ({totalQuantidade.toLocaleString('pt-BR')}) excede o limite de delimitação do coordenador ({maxQuantidade.toLocaleString('pt-BR')}).</>  
            ) : (
              <>O valor total de investimentos ({formatNumber(totalValor, { minimumFractionDigits: 2, maximumFractionDigits: 2, style: 'currency', currency: 'BRL' })}) excede o limite de delimitação do coordenador ({formatNumber(maxValor, { minimumFractionDigits: 2, maximumFractionDigits: 2, style: 'currency', currency: 'BRL' })}).</>  
            )}
          </Typography>
        </div>
      )}
      
      {delimitacaoCoordenador && delimitacaoCoordenador.length > 0 && (
        <div style={{ backgroundColor: '#E8F4FD', color: '#0C5460', padding: '10px', marginBottom: '15px', borderRadius: '4px', border: '1px solid #BEE5EB' }}>
          <Typography variant="body2">
            {maxQuantidade > 0 && (
              <div>Quantidade total: {totalQuantidade.toLocaleString('pt-BR')} / Limite do coordenador: {maxQuantidade.toLocaleString('pt-BR')}</div>
            )}
            {maxValor > 0 && (
              <div>Valor total: {formatNumber(totalValor, { minimumFractionDigits: 2, maximumFractionDigits: 2, style: 'currency', currency: 'BRL' })} / Limite do coordenador: {formatNumber(maxValor, { minimumFractionDigits: 2, maximumFractionDigits: 2, style: 'currency', currency: 'BRL' })}</div>
            )}
          </Typography>
        </div>
      )}

      <Grid
        container
        direction={'row'}
        justify={'space-between'}
        style={{ borderBottom: '1px solid #E5E5E5', paddingBottom: '20px' }}
      >
        <Grid item sm={columns}>
          <Typography variant="body1">{'CPF/CNPJ'}</Typography>
        </Grid>
        <Grid item sm={columns}>
          <Typography variant="body1">{'Conta Depósito'}</Typography>
        </Grid>
        <Grid item sm={columns}>
          <Typography variant="body1">{'Quantidade'}</Typography>
        </Grid>
        <Grid item sm={columns}>
          <Typography variant="body1">{'PU'}</Typography>
        </Grid>
        <Grid item sm={columns}>
          <Typography variant="body1">{'Comprovante'}</Typography>
        </Grid>
        <Grid item sm={columns}>
          <Typography variant="body1"></Typography>
        </Grid>
      </Grid>

      {ofertaPrimaria?.investidores.map((investidor: OfertaPrimariaInvestidor) => (
        <Grid
          container
          direction={'row'}
          justify={'space-between'}
          style={{ borderBottom: '1px solid #E5E5E5', paddingBottom: '20px', marginTop: '20px' }}
          alignItems={'center'}
        >
          <Grid item sm={columns}>
            <Typography variant="body2">{addCpfCnpjMask(investidor.cpfCnpj ?? '')}</Typography>
          </Grid>
          <Grid item sm={columns}>
            <Typography variant="body2">{investidor.contaDeposito}</Typography>
          </Grid>
          <Grid item sm={columns}>
            <Typography variant="body2">{investidor.quantidade}</Typography>
          </Grid>
          <Grid item sm={columns}>
            <Typography variant="body2">{formatEightDecimalsCurrency(investidor.precoUnitario)}</Typography>
          </Grid>
          <Grid item sm={columns}>
            <Typography variant="body2">
              <ComprovanteComponent
                investidor={investidor}
                anexo={attachments.find((att) => att.investidorId === investidor!.investidorId)}
                onAdd={(anexo) => onAttachments([...attachments, anexo])}
                onDelete={(anexo) =>
                  onAttachments(attachments.filter((att) => att.investidorId !== anexo.investidorId))
                }
              />
            </Typography>
          </Grid>
          <Grid item sm={columns} container justifyContent="flex-end">
            <Typography variant="body2">
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  handleOfertaPrimariaRemove(investidor);
                }}
              >
                <Delete fontSize="small" />
              </Button>
            </Typography>
          </Grid>
        </Grid>
      ))}

      <Grid
        container
        direction={'row'}
        justify={'space-between'}
        style={{
          marginTop: '20px',
          paddingTop: '20px',
          borderTop: 'none',
        }}
      >
        <Grid item sm={columns}>
          <Typography variant="body1"></Typography>
        </Grid>
        <Grid item sm={columns}>
          <Typography variant="body1"></Typography>
        </Grid>
        <Grid item sm={columns}>
          <Typography variant="body1"></Typography>
        </Grid>
        <Grid item sm={columns}>
          <Typography variant="body1"></Typography>
        </Grid>
        <Grid item sm={columns} container justifyContent="flex-end">
          <Typography variant="body1" style={{ fontWeight: 'bold', paddingRight: '10px' }}>
            {'VALOR TOTAL:'}
          </Typography>
          <Typography variant="body2">
            {formatNumber(
              ofertaPrimaria?.investidores.reduce(
                (total, investidor) => total + investidor.quantidade * investidor.precoUnitario,
                0
              ) ?? 0,
              { minimumFractionDigits: 2, maximumFractionDigits: 8, style: 'currency', currency: 'BRL' }
            )}
          </Typography>
        </Grid>
      </Grid>
      <SmSpace />
      <InvestidorDrawer
        open={openInvestidorDrawer}
        onClose={handleInvestidorDrawerClose}
        investidores={investidores}
        handleOfertaPrimariaSubmit={handleOfertaPrimariaSubmit}
        tipoInstrumentoFinanceiro={tipoInstrumentoFinanceiro}
      />
      <InformacoesBancariasAddDrawer
        custodianteRazaoSocial={currentParticipante.razaoSocial}
        bancosOptions={bancos}
        open={openInformacoesBancariasDrawer}
        isEdit={isEditInformacoesBancarias}
        onClose={handleCloseInformacoesBancariasDrawer}
        addinformacoesBancarias={handleAddinformacoesBancarias}
      />
      <InformacoesBancariasListDrawer
        open={openInformacoesBancariasListDrawer}
        custodiante={
          {
            razaoSocialInstituicaoCustodiante: currentParticipante.razaoSocial,
            cnpjInstituicaoCustodiante: currentParticipante.cnpjParticipante,
            informacoesBancarias: ofertaPrimaria?.informacoesBancarias,
          } as Custodiante
        }
        onClose={handleCloseInformacoesBancariasListDrawer}
        removeDadosBancarios={handleUpdateInformacoesBancarias}
        isEdit={false}
        dadosBancarios={handleOpenAddInformacoesBancariasDrawer}
      />
    </div>
  );
};

export default AdicionarInvestidor;
